import React, { useState, useRef, useEffect } from 'react';
import { Languages } from 'lucide-react';
import styles from '../DeviceSelector/DeviceSelector.module.css';

const LanguageSelector = ({ selectedLanguage, onLanguageChange, disabled, isLiveMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const languages = {
    featured: [
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Spanish' },
      { code: 'fr', name: 'French' },
      { code: 'de', name: 'German' },
    ],
    highAccuracy: [
      { code: 'ar', name: 'Arabic', description: 'Supports MSA, Gulf, Egypt and Levant dialects' },
      { code: 'pt', name: 'Portuguese', description: 'Supports Portugal and Brazil dialects' },
      { code: 'cmn', name: 'Mandarin', description: 'Supports Traditional/Simplified, multiple accents' },
      { code: 'es-bilingual', name: 'Spanish & English bilingual', description: 'Bilingual support with domain config' },
    ],
    other: [
      { code: 'ba', name: 'Bashkir' },
      { code: 'eu', name: 'Basque' },
      { code: 'be', name: 'Belarusian' },
      { code: 'bn', name: 'Bengali' },
      { code: 'bg', name: 'Bulgarian' },
      { code: 'yue', name: 'Cantonese' },
      { code: 'ca', name: 'Catalan' },
      { code: 'zh', name: 'Chinese' },
      { code: 'hr', name: 'Croatian' },
      { code: 'cs', name: 'Czech' },
      { code: 'da', name: 'Danish' },
      { code: 'nl', name: 'Dutch' },
      { code: 'eo', name: 'Esperanto' },
      { code: 'et', name: 'Estonian' },
      { code: 'fi', name: 'Finnish' },
      { code: 'gl', name: 'Galician' },
      { code: 'el', name: 'Greek' },
      { code: 'he', name: 'Hebrew' },
      { code: 'hi', name: 'Hindi' },
      { code: 'hu', name: 'Hungarian' },
      { code: 'id', name: 'Indonesian' },
      { code: 'ia', name: 'Interlingua' },
      { code: 'ga', name: 'Irish' },
      { code: 'it', name: 'Italian' },
      { code: 'ja', name: 'Japanese' },
      { code: 'ko', name: 'Korean' },
      { code: 'lv', name: 'Latvian' },
      { code: 'lt', name: 'Lithuanian' },
      { code: 'ms', name: 'Malay' },
      { code: 'mt', name: 'Maltese' },
      { code: 'mr', name: 'Marathi' },
      { code: 'mn', name: 'Mongolian' },
      { code: 'no', name: 'Norwegian' },
      { code: 'fa', name: 'Persian' },
      { code: 'pl', name: 'Polish' },
      { code: 'ro', name: 'Romanian' },
      { code: 'ru', name: 'Russian' },
      { code: 'sk', name: 'Slovakian' },
      { code: 'sl', name: 'Slovenian' },
      { code: 'sw', name: 'Swahili' },
      { code: 'sv', name: 'Swedish' },
      { code: 'ta', name: 'Tamil' },
      { code: 'th', name: 'Thai' },
      { code: 'tr', name: 'Turkish' },
      { code: 'uk', name: 'Ukrainian' },
      { code: 'ur', name: 'Urdu' },
      { code: 'ug', name: 'Uyghur' },
      { code: 'vi', name: 'Vietnamese' },
    ]
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // eslint-disable-next-line
  const getSelectedLanguageName = () => {
    const allLanguages = [...languages.featured, ...languages.highAccuracy, ...languages.other];
    const selected = allLanguages.find(lang => lang.code === selectedLanguage);
    return selected ? selected.name : 'Select Language';
  };

  const handleLanguageSelect = (code) => {
    onLanguageChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  const filterLanguages = (section) => {
    return section.filter(lang =>
      lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleArrowClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const renderOption = (lang, isDisabled = false) => (
    <div
      key={lang.code}
      className={`${styles.deviceOption} ${selectedLanguage === lang.code ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
      onClick={() => !isDisabled && handleLanguageSelect(lang.code)}
    >
      <div className={styles.deviceInfo}>
        <div className={styles.deviceLabel}>{lang.name}</div>
        {lang.description && (
          <div className={styles.defaultLabel}>{lang.description}</div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`${styles.deviceSelector}`}
      ref={dropdownRef}
    >
      <div className={styles.selectorContainer}>
        <div
          className={`${styles.combinedButton} ${isOpen ? styles.enabled : ''} ${disabled ? styles.disabled : ''}`}
        >
          <div
            className={styles.cameraSection}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            <Languages className={styles.icon} />
          </div>

          <div
            className={styles.dropdownSection}
            onClick={handleArrowClick}
          >
            <span className={`${styles.dropdownArrow} ${isOpen ? styles.open : ''}`}>
              ▼
            </span>
          </div>
        </div>

        {isOpen && (
          <div className={`${styles.dropdownContent} ${styles.third}`}>
            <div className={styles.searchWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search languages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className={styles.deviceList}>
              {filterLanguages(languages.featured).length > 0 && (
                <>
                  <div className={styles.sectionHeader}>Featured Languages</div>
                  {filterLanguages(languages.featured).map(lang => renderOption(lang))}
                </>
              )}

              {!isLiveMode && (
                <div className={styles.proNote}>
                  <div className={styles.proNoteHeader}>Live Mode Languages</div>
                  <p className={styles.proNoteText}>
                    Additional languages are available in Live Mode only.
                  </p>
                </div>
              )}

              {isLiveMode && filterLanguages(languages.highAccuracy).length > 0 && (
                <>
                  <div className={styles.sectionHeader}>Other High Accuracy</div>
                  {filterLanguages(languages.highAccuracy).map(lang => renderOption(lang))}
                </>
              )}

              {/* {filterLanguages(languages.other).length > 0 && (
                <>
                  <div className={styles.sectionHeader}></div>
                  {filterLanguages(languages.other).map(lang =>
                    renderOption(lang, !isLiveMode)
                  )}
                </>
              )} */}
            </div>

            {/* <button
              className={styles.refreshButton}
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm('');
              }}
            >
              Clear Search
            </button> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;